/**
 * CoPilot Store — Jotai atoms for the Operation Co-Pilot.
 *
 * Manages conversation state, sends messages to the backend,
 * and provides cached seller data as context.
 *
 * Supports SSE streaming: blocks appear instantly, narrative text
 * streams in token-by-token for a responsive feel.
 */

import { atom } from 'jotai'
import { listingsAtom } from './AmazonListingsStore'
import { ordersAtom } from './AmazonOrdersStore'

const API_BASE = import.meta.env.VITE_API_BASE || ''

// ─── Types (matching backend CoPilot protocol) ───────────────────────────────

export interface MetricCardBlock {
  type: 'metric_card'
  title: string
  value: string
  subtitle?: string
  trend?: { direction: 'up' | 'down' | 'flat'; label: string }
  icon?: string
}

export interface DataTableColumn {
  key: string
  label: string
  align?: 'left' | 'right' | 'center'
}

export interface DataTableBlock {
  type: 'data_table'
  title: string
  columns: DataTableColumn[]
  rows: Record<string, string | number>[]
  maxRows?: number
  sortable?: boolean
  filterColumn?: string
  layout?: 'table' | 'cards'
}

export interface ActionItem {
  id: string
  label: string
  description: string
  severity: 'info' | 'warning' | 'critical'
  actionType?: string
  actionParams?: Record<string, unknown>
}

export interface ActionListBlock {
  type: 'action_list'
  title: string
  items: ActionItem[]
}

export interface TextBlock {
  type: 'text'
  content: string
}

export interface AlertBlock {
  type: 'alert'
  severity: 'info' | 'warning' | 'error' | 'success'
  title: string
  message: string
}

export interface KeywordChipsBlock {
  type: 'keyword_chips'
  asin: string
  sku: string
  title: string
  currentKeywords: string
  suggestedKeywords: string[]
}

export interface ContentSuggestionItem {
  field: string
  label: string
  current: string
  suggested: string
  rationale?: string
}

export interface ContentSuggestionBlock {
  type: 'content_suggestion'
  asin: string
  sku: string
  productTitle: string
  suggestions: ContentSuggestionItem[]
}

export type UIBlock =
  | MetricCardBlock
  | DataTableBlock
  | ActionListBlock
  | TextBlock
  | AlertBlock
  | KeywordChipsBlock
  | ContentSuggestionBlock

export interface CoPilotMessage {
  id: string
  role: 'assistant'
  blocks: UIBlock[]
  textFallback: string
  toolsUsed?: string[]
  timestamp: number
}

export interface UserMessage {
  id: string
  role: 'user'
  content: string
  timestamp: number
}

export type ConversationMessage = CoPilotMessage | UserMessage

// ─── Suggested Operations (Welcome Screen) ──────────────────────────────────

export interface SuggestedOperation {
  label: string
  description: string
  prompt: string
  icon: 'chart' | 'search' | 'orders' | 'dollar' | 'document'
}

export const SUGGESTED_OPERATIONS: SuggestedOperation[] = [
  {
    label: 'Business Snapshot',
    description: "Today's orders, revenue, and listing health at a glance",
    prompt: 'Give me a snapshot of my business today',
    icon: 'chart',
  },
  {
    label: 'Listing Health Check',
    description: 'Find inactive listings, missing images, and issues',
    prompt: 'Analyze my listings for any issues that need attention',
    icon: 'search',
  },
  {
    label: 'Order Summary',
    description: 'Fulfillment status, top sellers, and order trends',
    prompt: "Summarize today's orders and fulfillment status",
    icon: 'orders',
  },
  {
    label: 'Pricing Analysis',
    description: 'Active sales, discount depths, and expiring promotions',
    prompt: 'Analyze my pricing — any products on sale or pricing issues?',
    icon: 'dollar',
  },
  {
    label: 'Content Audit',
    description: 'Check titles, descriptions, keywords for consistency and gaps',
    prompt:
      'Audit my listing content — check if titles, descriptions, and keywords are consistent and suggest improvements',
    icon: 'document',
  },
]

// ─── Core Atoms ─────────────────────────────────────────────────────────────

/** Conversation messages (user + assistant) */
export const conversationAtom = atom<ConversationMessage[]>([])

/** Current input text */
export const inputAtom = atom('')

/** Whether the AI is responding (true from request start to stream complete) */
export const isLoadingAtom = atom(false)

/** Error message if something went wrong */
export const errorAtom = atom<string | null>(null)

// ─── Derived Atoms ──────────────────────────────────────────────────────────

/** Whether the conversation has started (determines welcome screen vs chat view) */
export const hasStartedAtom = atom((get) => get(conversationAtom).length > 0)

// ─── SSE Parser ─────────────────────────────────────────────────────────────

interface SSEEvent {
  event: string
  data: string
}

/** Parse SSE text into events. Returns parsed events and remaining buffer. */
function parseSSE(buffer: string): { events: SSEEvent[]; remaining: string } {
  const events: SSEEvent[] = []
  const parts = buffer.split('\n\n')
  const remaining = parts.pop() || '' // Last part may be incomplete

  for (const part of parts) {
    if (!part.trim()) continue
    let event = ''
    let data = ''

    for (const line of part.split('\n')) {
      if (line.startsWith('event: ')) event = line.slice(7).trim()
      else if (line.startsWith('data: ')) data = line.slice(6)
    }

    if (data) {
      events.push({ event, data })
    }
  }

  return { events, remaining }
}

// ─── Action Atoms ───────────────────────────────────────────────────────────

let msgCounter = 0
function nextId() {
  return `msg-${Date.now()}-${++msgCounter}`
}

/** Send a message to the CoPilot (with SSE streaming) */
export const sendMessageAtom = atom(null, async (get, set, userText: string) => {
  const text = userText.trim()
  if (!text) return

  // Add user message to conversation
  const userMsg: UserMessage = {
    id: nextId(),
    role: 'user',
    content: text,
    timestamp: Date.now(),
  }

  const prev = get(conversationAtom)
  set(conversationAtom, [...prev, userMsg])
  set(inputAtom, '')
  set(isLoadingAtom, true)
  set(errorAtom, null)

  try {
    // Build message history for API (simplified role/content)
    const allMessages = [...prev, userMsg]
    const apiMessages = allMessages.map((m) => ({
      role: m.role as 'user' | 'assistant',
      content: m.role === 'user' ? m.content : (m as CoPilotMessage).textFallback,
    }))

    // Send cached seller data as context (avoids SP-API re-fetches on backend)
    const listings = get(listingsAtom)
    const orders = get(ordersAtom)

    const res = await fetch(`${API_BASE}/copilot/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        messages: apiMessages,
        context: {
          listings,
          orders,
        },
      }),
    })

    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error((data as any).error || `Request failed (${res.status})`)
    }

    // ── Stream SSE response ─────────────────────────────────────────
    const reader = res.body?.getReader()
    if (!reader) {
      throw new Error('Response body is not readable')
    }

    const decoder = new TextDecoder()
    let sseBuffer = ''
    const messageId = `cp-${Date.now()}`
    let toolBlocks: UIBlock[] = []
    let toolsUsed: string[] = []
    let textContent = ''
    let messageCreated = false

    // Helper: build a CoPilotMessage from current streaming state
    function buildMessage(): CoPilotMessage {
      const textBlock: TextBlock | null = textContent
        ? { type: 'text', content: textContent }
        : null
      const blocks = textBlock
        ? [textBlock, ...toolBlocks]
        : [...toolBlocks]

      return {
        id: messageId,
        role: 'assistant',
        blocks,
        textFallback: textContent,
        toolsUsed: toolsUsed.length > 0 ? toolsUsed : undefined,
        timestamp: Date.now(),
      }
    }

    // Helper: update conversation with current streaming message
    function upsertMessage() {
      const msg = buildMessage()
      if (messageCreated) {
        // Replace last message
        const conv = get(conversationAtom)
        set(conversationAtom, [...conv.slice(0, -1), msg])
      } else {
        // Add new message
        set(conversationAtom, [...get(conversationAtom), msg])
        messageCreated = true
      }
    }

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      sseBuffer += decoder.decode(value, { stream: true })

      // Parse complete SSE events from buffer
      const { events, remaining } = parseSSE(sseBuffer)
      sseBuffer = remaining

      for (const evt of events) {
        if (evt.event === 'blocks') {
          try {
            const parsed = JSON.parse(evt.data)
            toolBlocks = parsed.blocks || []
            toolsUsed = parsed.toolsUsed || []
            // Show blocks immediately (cards display skeleton while AI enriches)
            upsertMessage()
          } catch {}
        } else if (evt.event === 'blocks_update') {
          try {
            const parsed = JSON.parse(evt.data)
            toolBlocks = parsed.blocks || []
            // Re-render with enriched data (skeletons replaced with content)
            upsertMessage()
          } catch {}
        } else if (evt.event === 'text_delta') {
          try {
            const parsed = JSON.parse(evt.data)
            if (parsed.content) {
              textContent += parsed.content
              upsertMessage()
            }
          } catch {}
        } else if (evt.event === 'error') {
          try {
            const parsed = JSON.parse(evt.data)
            throw new Error(parsed.message || 'Something went wrong')
          } catch (e) {
            if (e instanceof Error && e.message !== 'Something went wrong') throw e
            throw new Error('Something went wrong')
          }
        }
        // 'done' event — nothing extra needed, loop will end
      }
    }

    // If no message was created (edge case), create an empty one
    if (!messageCreated) {
      upsertMessage()
    }
  } catch (err) {
    set(errorAtom, err instanceof Error ? err.message : 'Something went wrong')
  } finally {
    set(isLoadingAtom, false)
  }
})

/** Reset the conversation */
export const resetConversationAtom = atom(null, (_get, set) => {
  set(conversationAtom, [])
  set(errorAtom, null)
  set(inputAtom, '')
})
